import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import LoginPage from './pages/LoginPage';
import LoadingIndicator from './components/LoadingIndicator';
import './styles/App.css';
import MyPatientsPage from './pages/MyPatientsPage';
import PatientDetailPage from './pages/PatientDetailPage';
import AlertsPage from './pages/AlertsPage';
import MessagesPage from './pages/MessagesPage';
import AppointmentsPage from './pages/AppointmentsPage';
import MedicationsPage from './pages/MedicationsPage';
import AdminPanel from './pages/Administrator';
import MyDashboard from './pages/MyDashboard';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import HelpPage from './pages/HelpPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ConsentFormPage from './pages/ConsentFormPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import DeleteAccount from './pages/DeleteAccount';
import OnlineConsultationPage from './pages/OnlineConsultationPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import NotFoundPage from './pages/NotFoundPage';
import { getToken } from './utils/auth';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { ToastProvider } from './context/ToastContext';
import { OverlayProvider } from './context/OverlayContext';
import { ChatWebSocketProvider } from './context/ChatWebSocketContext';
import { subscribeForPush } from './utils/push';
import CookieConsentBanner from './components/CookieConsentBanner';
import MotionProvider from './motion/MotionProvider';
import { PageTransition } from './motion';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: 1,
    },
  },
});

function PrivateRoute({ element }) {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated === null) {
    return <LoadingIndicator fullScreen message="Loading..." />;
  }
  return isAuthenticated ? element : <Navigate to="/" replace state={{ from: 'auth-required' }} />;
}

function HomeRoute() {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated === null) {
    return <LoadingIndicator fullScreen message="Loading..." />;
  }
  if (isAuthenticated) {
    return <Navigate to="/mydashboard" replace />;
  }
  return <LoginPage />;
}

function AdminRoute({ element }) {
  const { isAuthenticated, userType } = useAuth();

  if (isAuthenticated === null) {
    return <LoadingIndicator fullScreen message="Loading..." />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/" replace state={{ from: 'auth-required' }} />;
  }
  return userType === 'admin' ? element : <Navigate to="/mydashboard" replace />;
}

function PushRegistration() {
  const { isAuthenticated, user } = useAuth();
  const registeredKeyRef = React.useRef('');
  const isSubscribingRef = React.useRef(false);
  useEffect(() => {
    if (isAuthenticated !== true) return;
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) return;
    const token = getToken();
    if (!token) return;
    const userKey = `${user?.user_id ?? 'unknown'}:${token.slice(-16)}`;
    if (registeredKeyRef.current === userKey) return;
    const trySubscribe = () => {
      if (isSubscribingRef.current) return;
      isSubscribingRef.current = true;
      subscribeForPush(apiUrl, getToken)
        .then(() => {
          registeredKeyRef.current = userKey;
        })
        .catch(() => {})
        .finally(() => {
          isSubscribingRef.current = false;
        });
    };
    if (typeof Notification === 'undefined') {
      trySubscribe();
      return;
    }
    if (Notification.permission === 'granted') {
      trySubscribe();
      return;
    }
    const onFirstInteraction = () => {
      trySubscribe();
      window.removeEventListener('click', onFirstInteraction, true);
      window.removeEventListener('keydown', onFirstInteraction, true);
      window.removeEventListener('touchstart', onFirstInteraction, true);
    };
    window.addEventListener('click', onFirstInteraction, true);
    window.addEventListener('keydown', onFirstInteraction, true);
    window.addEventListener('touchstart', onFirstInteraction, true);
    return () => {
      window.removeEventListener('click', onFirstInteraction, true);
      window.removeEventListener('keydown', onFirstInteraction, true);
      window.removeEventListener('touchstart', onFirstInteraction, true);
    };
  }, [isAuthenticated, user?.user_id]);

  useEffect(() => {
    if (isAuthenticated !== true) return;
    if (!('serviceWorker' in navigator)) return;
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) return;
    const onSwMessage = (event) => {
      if (event?.data?.type !== 'PUSH_SUBSCRIPTION_CHANGE') return;
      subscribeForPush(apiUrl, getToken).catch(() => {});
    };
    navigator.serviceWorker.addEventListener('message', onSwMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', onSwMessage);
    };
  }, [isAuthenticated]);
  return null;
}

function AppRoutes() {
  const location = useLocation();

  return (
    <div className="main-wrapper">
      <div className="app">
        <div className="main-content">
          <PageTransition className="min-h-screen">
            <Routes location={location}>
              <Route path="/" element={<HomeRoute />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/privacy-policy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/consent" element={<ConsentFormPage />} />
              <Route path="/delete-account" element={<DeleteAccount />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              <Route path="/mydashboard" element={<PrivateRoute element={<MyDashboard />} />} />
              <Route path="/my-patients" element={<PrivateRoute element={<MyPatientsPage />} />} />
              <Route path="/patient/:patientId" element={<PrivateRoute element={<PatientDetailPage />} />} />
              <Route path="/alerts" element={<PrivateRoute element={<AlertsPage />} />} />
              <Route path="/messages" element={<PrivateRoute element={<MessagesPage />} />} />
              <Route path="/appointments" element={<PrivateRoute element={<AppointmentsPage />} />} />
              <Route path="/medications" element={<PrivateRoute element={<MedicationsPage />} />} />
              <Route path="/change-password" element={<PrivateRoute element={<ChangePasswordPage />} />} />
              <Route path="/online-consultation" element={<PrivateRoute element={<OnlineConsultationPage />} />} />
              <Route path="/administrator" element={<AdminRoute element={<AdminPanel />} />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </PageTransition>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <MotionProvider>
        <AuthProvider>
          <NotificationProvider>
            <ToastProvider>
              <OverlayProvider>
                <ChatWebSocketProvider>
                  <PushRegistration />
                  <AppRoutes />
                  <CookieConsentBanner />
                </ChatWebSocketProvider>
              </OverlayProvider>
            </ToastProvider>
          </NotificationProvider>
        </AuthProvider>
      </MotionProvider>
    </QueryClientProvider>
  );
}

export default App;
